/**
 * @file src/offline/offlineLibrary.js
 * @description Downloading a collection, removing one, and answering what is actually here.
 *
 * The two stores underneath each answer half the question and neither is asked to answer the other
 * half. `offlineLibraryStore.js` holds the INTENT — which collections were asked for, which files
 * they need, which release they were pinned at. `audioCacheStore.js` holds the FACT — which bytes
 * exist, and how many things are holding each one. This module is the only place the two meet, and
 * the rules it enforces all follow from keeping them apart:
 *
 * **Ready means the bytes are there.** Never "the manifest says so". A browser can reclaim an
 * origin's storage whole and tell nobody, so every readiness answer re-checks the bucket.
 *
 * **Removal unpins; it does not delete.** Two collections sharing a track share one entry, and
 * deleting it because one of them left would silence the other. Unpinning hands the bytes back to
 * the play cache, which evicts them in its own time — the last holder leaving is what frees the
 * space, and the refcount is what knows.
 *
 * **A download pins what is already there.** A track in the play cache is not fetched again; its
 * existing entry is pinned in place. That is the whole reason the download and the play cache share
 * one bucket and one key scheme.
 *
 * **Interruption is normal.** A closed tab mid-download leaves a manifest whose entries are partly
 * present. Re-opening re-checks presence and fetches only what is missing — resume by presence,
 * with no separate progress record to fall out of step with the bytes.
 */
import {
  currentAudioCacheGeneration,
  pinCacheEntries,
  readPinnedCacheEntries,
  readPresentCacheKeys,
  unpinCacheEntries,
  writePinnedCacheEntry,
} from '../audio/playback/audioCacheStore.js';
import { getRoomGrantHeaders } from '../room/roomGrant.js';
import { resolveApiBase } from '../api/httpClient.js';
import {
  DOWNLOAD_STATE,
  RELEASE_KIND,
  deleteDownloadedCollection,
  listDownloadedCollections,
  patchDownloadedCollection,
  pruneStoredReleases,
  readConfigurationOwnerKey,
  readDownloadedCollection,
  readHeldPinnedKeys,
  readStoredRelease,
  releaseRecordKey,
  StaleIdentityError,
  writeDownloadedCollection,
  writeStoredReleases,
} from './offlineLibraryStore.js';
import {
  buildCollectionManifest,
  collectionCoverAsset,
  manifestAssets,
  manifestQuality,
  repairTrackManifestEntry,
  resolveDefaultPairManifestDependency,
} from './collectionManifest.js';
import {
  fetchEntangledWorldRelease,
  fetchOrbiterRelease,
  fetchTrackRelease,
} from '../api/dataManager/loaders.js';
import { precacheUiAssets } from './uiAssetPrecache.js';
import { buildOfflineEntryUrl } from './offlineEntryUrl.js';
import {
  DEFAULT_OFFLINE_QUALITY,
  normalizeOfflineQuality,
  OFFLINE_MAX_BYTES,
  OFFLINE_MAX_COLLECTIONS,
} from './offlineConfig.js';
import { measureAssets } from './offlineSizes.js';
import { toCacheRequestUrl } from './offlineKeys.js';
import { notifyOfflineWorkerLibraryChanged } from './registerOfflineWorker.js';
import { makeScopeKey, orbiterScopeKey } from '../input/midi/scopeKey.js';

/** Raised when a download cannot start, or has to stop, because a limit says so. */
export class OfflineCapError extends Error {
  constructor(reason, detail = {}) {
    super(`Offline download refused: ${reason}`);
    this.name = 'OfflineCapError';
    this.reason = reason;
    this.detail = detail;
  }
}

// ── Listeners ────────────────────────────────────────────────────────────────────────────────────

const listeners = new Set();

/**
 * Two kinds of change, and telling them apart is not a nicety.
 *
 * `progress` fires once per file — dozens of times a minute — and everything it reports is already
 * in memory. `library` fires when something structural happened: a download finished, a collection
 * was removed, an identity was reconciled.
 *
 * They started out as one signal, and a reader that answered both by re-reading the store turned a
 * download into an O(assets²) sweep of Cache Storage: every file stored triggered a presence check
 * of every file in the manifest, each one queued behind the download's own writes. It froze the
 * page. So the kind travels with the notification, and a reader is expected to answer a `progress`
 * tick from memory.
 */
export const OFFLINE_CHANGE = { PROGRESS: 'progress', LIBRARY: 'library' };

/**
 * Subscribe to library changes. The listener receives one of {@link OFFLINE_CHANGE} — see above for
 * why a `progress` tick must not be answered with a store read.
 */
export function subscribeToOfflineLibrary(listener) {
  if (typeof listener !== 'function') return () => {};
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function notify(kind = OFFLINE_CHANGE.LIBRARY) {
  // The worker serves pinned bytes by looking a remote URL up in the stored manifests, so a change
  // to WHAT is downloaded is a change it needs to see. Said here rather than at each call site: this
  // is already the one place every download, resume, removal and backfill reports itself.
  if (kind === OFFLINE_CHANGE.LIBRARY) notifyOfflineWorkerLibraryChanged();
  for (const listener of [...listeners]) {
    try {
      listener(kind);
    } catch (error) {
      console.warn('[offline] A library listener threw.', error);
    }
  }
}

// ── In-flight downloads ──────────────────────────────────────────────────────────────────────────

/**
 * One run per collection. Keyed rather than a single slot: a person may start a second download
 * while the first is going, and the progress each reports has to stay its own.
 */
const running = new Map();

/** A live snapshot of progress, for the UI. Not persisted — a closed tab resumes by presence. */
const progress = new Map();

/** What a download is doing right now, or null when it is not running. */
export function readDownloadProgress(collectionId) {
  return progress.get(collectionId) ?? null;
}

function setProgress(collectionId, patch) {
  const current = progress.get(collectionId) || {};
  progress.set(collectionId, { ...current, ...patch });
  notify(OFFLINE_CHANGE.PROGRESS);
}

// ── Availability snapshot ────────────────────────────────────────────────────────────────────────

/**
 * Which logical keys are pinned, cached in memory.
 *
 * A synchronous answer is required by exactly one caller and it is not a UI one: the playback
 * strategy has to decide buffered-versus-streamed while building a voice, and it cannot await a
 * store. Kept honest by being refreshed on every library change rather than trusted indefinitely —
 * and a stale "yes" is a buffered load that falls back to the network, not a broken one.
 */
let pinnedKeySnapshot = new Set();
let pinnedBytes = 0;

/** Whether these bytes are pinned, answered from the in-memory snapshot. */
export function isKeyPinnedSync(cacheRequestUrl) {
  return Boolean(cacheRequestUrl) && pinnedKeySnapshot.has(cacheRequestUrl);
}

/**
 * Re-read the pinned set, intersected with what the bucket actually holds.
 *
 * The intersection is the point. A pin row can outlive its bytes — a browser may reclaim Cache
 * Storage and leave IndexedDB alone — and this snapshot's one consumer forces the BUFFERED sink on
 * the strength of it, overriding the feasibility guards that exist to stop a long track being fully
 * downloaded over a slow connection. A "yes" that means only "the ledger remembers pinning this"
 * would spend exactly the wait those guards prevent. Every other reader of this store already
 * re-checks the bucket before believing the manifest; this one now does too.
 */
export async function refreshOfflineSnapshot() {
  try {
    const pinned = await readPinnedCacheEntries();
    const present = await readPresentCacheKeys([...pinned.keys()]);
    pinnedKeySnapshot = present;
    pinnedBytes = 0;
    for (const [key, bytes] of pinned) {
      if (present.has(key)) pinnedBytes += Number(bytes) || 0;
    }
  } catch (error) {
    console.warn('[offline] Could not read the pinned entries.', error);
  }
  notify();
  return { keys: pinnedKeySnapshot, bytes: pinnedBytes };
}

// ── Storage budget ───────────────────────────────────────────────────────────────────────────────

/**
 * Ask the browser to keep this origin's storage rather than reclaim it under pressure.
 *
 * Asked once, on the first download, because that is the first moment there is anything worth
 * keeping and the first moment the request means something to the person being asked. A refusal is
 * not an error: downloads still work, they are just evictable — which is why nothing here ever
 * trusts the manifest over the bucket.
 * @returns {Promise<boolean>} whether storage is now persisted
 */
export async function requestPersistentStorage() {
  try {
    if (!navigator?.storage?.persist) return false;
    if (await navigator.storage.persisted?.()) return true;
    return await navigator.storage.persist();
  } catch {
    return false;
  }
}

/**
 * What THIS person's downloads occupy: the bytes of every key their own collections hold.
 *
 * Not the ledger's total, which is one browser's worth and may include somebody else's downloads —
 * the cap is per person, and being refused because of a library you cannot see is not a refusal
 * anybody could act on. A key two of their own collections share is counted once (it is one copy);
 * a key shared with the OTHER person on this browser is counted for both, because both are holding
 * it and either removing theirs leaves it there for the other.
 *
 * What counts is what this person's manifests RECORD pinning — never merely what they name. A
 * manifest names the assets it wants, and wanting is not holding: a collection whose download never
 * got as far as pinning anything would otherwise be charged for bytes the OTHER person on this
 * browser pinned, and refused its own download over an allowance it had not spent. A run in flight
 * is the one case where a pin exists that no manifest has recorded yet, so it passes its own keys
 * in rather than having them guessed at.
 *
 * A key two of this person's collections share is counted once — it is one copy. Only keys the
 * ledger actually has bytes for count at all.
 *
 * @param {Iterable<string>} [alsoHeld] keys the caller has pinned this run but not yet persisted
 */
async function readOwnerUsedBytes(alsoHeld = []) {
  const [manifests, pinned] = await Promise.all([listDownloadedCollections(), readPinnedCacheEntries()]);
  const keys = new Set(alsoHeld);
  for (const manifest of manifests) {
    for (const key of manifest?.pinnedKeys || []) keys.add(key);
  }
  let total = 0;
  for (const key of keys) total += Number(pinned.get(key)) || 0;
  return total;
}

/** Every key this person's own manifests record a pin on — the keys their allowance already pays for. */
async function readOwnerPinnedKeys() {
  const manifests = await listDownloadedCollections();
  const keys = new Set();
  for (const manifest of manifests) {
    for (const key of manifest?.pinnedKeys || []) keys.add(key);
  }
  return keys;
}

/**
 * What removing this collection would actually free from this person's allowance.
 *
 * NOT the collection's `bytes`, which is its gross footprint and counts keys other collections are
 * holding too — removing it frees none of those. Only the keys no other manifest of THIS person
 * records come back. Offering the gross number as reclaimable space is advice that does not work:
 * someone told to remove a 620 MB collection to make room can find themselves with 40 MB more.
 *
 * It is also not disk space. Removal unpins; it does not delete. Unpinned bytes become evictable,
 * and the browser reclaims them when it wants the room — so this is the allowance coming back, which
 * is the number a cap refusal is actually about.
 *
 * @param {string} collectionId
 * @returns {Promise<number>}
 */
export async function readRemovalSavings(collectionId, context = null) {
  // The two reads are handed in when a caller is already looping — `readOfflineLibrary` asks this of
  // every collection it lists, and re-reading both tables per row turned one refresh into twenty.
  const manifests = context?.manifests ?? (await listDownloadedCollections());
  const mine = manifests.find((manifest) => manifest?.collectionId === collectionId);
  if (!mine) return 0;

  const heldElsewhere = new Set();
  for (const manifest of manifests) {
    if (manifest?.collectionId === collectionId) continue;
    for (const key of manifest?.pinnedKeys || []) heldElsewhere.add(key);
  }

  const pinned = context?.pinned ?? (await readPinnedCacheEntries());
  let total = 0;
  for (const key of mine.pinnedKeys || []) {
    if (heldElsewhere.has(key)) continue;
    total += Number(pinned.get(key)) || 0;
  }
  return total;
}

/**
 * Used-of-allowed, plus what the browser thinks of the whole origin.
 *
 * `usedBytes` is what this person's own downloads occupy, because that is what their cap is about
 * and what removing one of their collections changes. `quota`/`originUsage` come from `estimate()`
 * and describe the browser's own accounting of everything this origin stores — every account's
 * downloads, the play cache, the interface assets — which is a different (larger) number. Both are
 * reported rather than blended: the physical space is shared even though the allowance is not, and
 * a person shown one figure that is secretly two would have no way to understand a refusal.
 */
export async function readStorageStatus() {
  let originUsage = null;
  let quota = null;
  try {
    const estimate = await navigator?.storage?.estimate?.();
    originUsage = Number.isFinite(estimate?.usage) ? estimate.usage : null;
    quota = Number.isFinite(estimate?.quota) ? estimate.quota : null;
  } catch {
    // An origin that will not estimate still downloads; only the readout goes quiet.
  }
  return {
    // Read from the ledger, not from the in-memory snapshot. The snapshot exists for one
    // synchronous caller and is refreshed when a download ENDS; a readout driven by it sat at zero
    // for the whole of a running download and only caught up at the end, which is exactly the
    // moment a used-of-allowed figure stops being interesting.
    usedBytes: await readOwnerUsedBytes(),
    maxBytes: OFFLINE_MAX_BYTES,
    originUsage,
    quota,
    persisted: Boolean(await navigator?.storage?.persisted?.().catch(() => false)),
  };
}

// ── Fetching bytes ───────────────────────────────────────────────────────────────────────────────

function isPlatformAudioUrl(url) {
  const base = resolveApiBase();
  if (!base || !url) return false;
  return String(url).startsWith(`${String(base).replace(/\/+$/, '')}/tracks/`);
}

/**
 * Fetch one asset and store it pinned — unless it will not fit.
 *
 * The budget is checked against the response's declared size BEFORE the body is read, which is what
 * makes the cap a refusal rather than an overshoot: a file is either wholly downloaded or not
 * started, never stored and then found to be over. A server that declines to state a size is
 * checked after the read instead and the oversized blob is simply not stored — one wasted transfer
 * is the cost of not trusting a number nobody sent.
 *
 * The body is read to a Blob rather than the response being cloned: a clone makes the browser
 * buffer both branches of a streaming body for the whole download, and the size is wanted anyway.
 * Storing it back under an explicit `content-length` is also what lets the boot reconcile size this
 * entry later without reading the body again.
 *
 * @param {{ remainingBytes: number, takePin?: boolean }} budget how much room is left under the
 *   cap, and whether this collection still needs to TAKE a pin — false when it already holds one
 *   and is only replacing bytes that were reclaimed.
 * @returns {Promise<{ bytes: number, refused: boolean }>}
 */
async function fetchAndPin(assetRef, { signal, generation, remainingBytes, takePin = true }) {
  const platformAudio = isPlatformAudioUrl(assetRef.url);
  const response = await fetch(assetRef.url, {
    signal,
    // The audio proxy authorizes per request, and inside a room the grant travels in a header —
    // exactly as playback reaches the same bytes. A public texture needs neither.
    credentials: platformAudio ? 'include' : 'same-origin',
    ...(platformAudio ? { headers: getRoomGrantHeaders() } : {}),
  });

  if (!response.ok) {
    const error = new Error(`Failed to download asset: ${response.status} ${response.statusText}`);
    error.status = response.status;
    error.assetKind = assetRef.kind;
    throw error;
  }

  const declared = Number(response.headers.get('content-length'));
  if (Number.isFinite(declared) && declared > 0 && declared > remainingBytes) {
    return { outcome: 'refused-cap', bytes: 0 };
  }

  const blob = await response.blob();
  if (blob.size > remainingBytes) {
    return { outcome: 'refused-cap', bytes: 0 };
  }

  const headers = new Headers();
  const contentType = response.headers.get('content-type') || blob.type;
  if (contentType) headers.set('content-type', contentType);
  headers.set('content-length', String(blob.size));

  const stored = await writePinnedCacheEntry(
    assetRef.key,
    new Response(blob, { status: 200, headers }),
    blob.size,
    generation,
    { takePin },
  );
  // Three outcomes, not two. A refused write — a stale generation because the identity changed
  // mid-download, or a store that would not open — is NOT a zero-byte success: counting it as one
  // let a download whose every remaining write was refused finish reporting itself complete, and
  // record a pin on bytes that were never stored.
  return { outcome: stored ? 'stored' : 'not-stored', bytes: stored ? blob.size : 0 };
}

// ── The lifecycle queue ──────────────────────────────────────────────────────────────────────────

/**
 * Every download, resume and removal runs here, one at a time.
 *
 * These operations all read a shared total, decide against it, and then write — and running two of
 * them at once broke that in three separate ways at once. Two downloads each read the same free
 * space and each admitted a file that fit "the" remaining budget, landing well over the cap; two
 * starts at nine collections each saw room and both created an eleventh; two removals each read the
 * same manifest before either deleted it, and between them released two pins for the one they held,
 * taking a shared track away from the collection that still wanted it.
 *
 * Each of those has its own narrow fix, and all three are the same bug: read-decide-write with no
 * exclusion. One queue answers it once. The cost is that a second download waits for the first,
 * which is no loss at all — the backend hydration is the bottleneck and running them in parallel
 * only made the progress figures untrue.
 *
 * ## The queue is per DOCUMENT, and the store is not
 *
 * A promise chain excludes callers inside one page, and the library is not inside one page: the
 * studio and the downloads library are separate documents on this origin. Their queues know nothing
 * of each other, so two tabs can still race — and that is a real, known gap.
 *
 * A `navigator.locks` wrapper was tried here and taken out again. A download holds the lifecycle for
 * as long as it runs, which can be twenty minutes, so the lock had to be given up on after a wait or
 * a Remove in the other tab would sit there looking broken. Giving up means running the work without
 * it, which is the race back — now arriving predictably rather than rarely, and behind a stall. A
 * guarantee that lapses under exactly the load it exists for is worse than none, because everything
 * downstream is written as though it held. Closing this properly means holding a lock across the
 * short read-decide-write phases only and leaving the transfer outside it, with the run's pins
 * journalled durably rather than kept in memory — a change to make on its own, not beside a feature.
 */
let lifecycleQueue = Promise.resolve();

function serializeLifecycle(work) {
  const settled = lifecycleQueue.then(work, work);
  lifecycleQueue = settled.then(
    () => undefined,
    () => undefined,
  );
  return settled;
}

/**
 * Which identity the library currently belongs to.
 *
 * Bumped whenever the stores are cleared or reconciled. A download that began under one identity
 * and finishes after another has arrived would otherwise write its manifest, its releases and its
 * pins into the new person's library — a logout that the download quietly undid. Every write below
 * re-checks the epoch it started under, and a mismatch abandons the run instead.
 */
let identityEpoch = 0;

/** Invalidate every in-flight run and start a new epoch. Called when the identity changes. */
export function invalidateOfflineRuns() {
  identityEpoch += 1;
  for (const [, run] of running) {
    try {
      run.controller.abort();
    } catch {
      // An abort that will not take still cannot write: the epoch check refuses it.
    }
  }
  running.clear();
  progress.clear();
  pinnedKeySnapshot = new Set();
  pinnedBytes = 0;
  notify();
}

function assertIdentity(epoch) {
  if (epoch !== identityEpoch) throw new StaleIdentityError();
}

/**
 * Refuse to carry on for anybody but the person this run started for.
 *
 * The epoch above only moves when something calls {@link invalidateOfflineRuns}, and not every path
 * that changes who the library belongs to does: a boot that resolves a viewer reconciles the owner
 * row on its own. So a download can outlive its owner with the epoch untouched — and every write
 * below derives its owner from that row, waits for the new reconcile, and stamps the arriving
 * person on the leaving person's download.
 *
 * The store refuses those writes on its own (each takes `expectedOwnerKey`), which is what stops the
 * misfiling. This is how the RUN finds out: without it the download would carry on to pin bytes no
 * manifest is left to record, and a pin nothing records is one nothing can ever release. Throwing
 * here lands in the same handler an aborted identity does, which hands those pins back.
 *
 * A run with no captured owner at all is refused rather than waved through. `null` comes back from
 * one place only — a library that would not open — so it does not mean "anonymous", it means the
 * capture failed; every write this run makes would then carry no guard and land under whoever is
 * current by the time it does. A store that cannot be read is a run that cannot record anything,
 * and carrying on would pin bytes into a bucket that opens perfectly well with no manifest to name
 * them.
 */
async function assertOwner(expectedOwnerKey) {
  if (!expectedOwnerKey || (await readConfigurationOwnerKey()) !== expectedOwnerKey) {
    throw new StaleIdentityError();
  }
}

// ── Download ─────────────────────────────────────────────────────────────────────────────────────

/**
 * Fetch, or pin in place, every asset a manifest names — and stop at the byte cap.
 *
 * The cap is checked as the download runs rather than predicted before it: nothing states an
 * asset's size until it has been fetched, and a guess that refused a download which would have fit
 * (or admitted one that did not) is worse than an honest stop partway. A collection stopped by the
 * cap is `partial`, which is a resumable state, not a broken one — deleting another collection and
 * re-opening this one finishes it.
 *
 * ## One pin per collection, ever — recorded as it is taken
 *
 * `held` is the set of keys this collection already holds a pin on, and nothing in it is pinned
 * again. Without it a resumed download raised the refcount a second time on everything the first
 * pass had stored, and the removal — which releases one pin per key — could then never bring those
 * entries back to zero: bytes that were both unreachable and unfreeable.
 *
 * Recording the pins only when the run FINISHED had the same effect by another route. A cancel
 * threw past the record, so the next attempt saw the keys as unheld and pinned them again. So each
 * pin is handed to `onPinned` the moment it is taken, and the caller persists it — including on the
 * way out of an abort.
 *
 * A key that is held but whose LEDGER ROW has gone falls through to the fetch taking a pin back:
 * the row is where the pin lived, so losing it lost the pin. Losing only the bytes does not — the
 * play cache evicts bytes and keeps the row — so that case keeps the pin it already has.
 *
 * @param {Set<string>} held keys this collection already holds a pin on
 * @param {(key: string) => void} onPinned called as each new pin is taken, before anything else
 */
async function realizeAssets(collectionId, assets, { signal, epoch, held = new Set(), onPinned }) {
  const generation = currentAudioCacheGeneration();
  const keys = assets.map((item) => item.key);
  const present = await readPresentCacheKeys(keys);
  // The ledger's view: which of these keys still has a pin record behind it.
  const pinned = await readPinnedCacheEntries();

  // Already-cached bytes are pinned, never refetched. This is the case the shared bucket exists
  // for: a track someone has been playing all week costs a pin, not a download.
  const alreadyHere = assets.filter((item) => present.has(item.key));
  const toPin = alreadyHere.filter((item) => !held.has(item.key)).map((item) => item.key);

  // `pinCacheEntries` asks the LEDGER; `readPresentCacheKeys` asked the BUCKET. An entry with bytes
  // but no ledger row — a write whose body landed and whose row did not, before the next boot
  // adopts it — cannot be pinned, and counting it as done would leave the collection "ready" while
  // the play cap was still free to evict it. So whatever could not be pinned is refetched instead.
  const unpinnable = new Set();
  // What this run has pinned but no manifest has recorded yet — the budget below has to know.
  const takenThisRun = new Set(held);
  if (toPin.length) {
    const outcome = await pinCacheEntries(toPin);
    // Recorded BEFORE the identity is re-checked. The refcount is already raised by the call
    // above, so a check that throws in between would leave a pin nothing knows it holds — and
    // therefore nothing can ever release.
    for (const key of outcome.pinned) {
      onPinned(key);
      takenThisRun.add(key);
    }
    for (const key of outcome.missing) unpinnable.add(key);
    assertIdentity(epoch);
  }

  const settled = alreadyHere.filter((item) => !unpinnable.has(item.key));
  const missing = assets.filter((item) => !present.has(item.key) || unpinnable.has(item.key));
  const total = assets.length;
  let completed = settled.length;
  let bytes = 0;
  const failed = [];
  /**
   * Assets marked `optional` that could not be fetched. Kept apart from `failed` because `failed` is
   * what decides a collection is not downloaded, and decoration must not be able to decide that — a
   * sky the platform ships in one detail level instead of two would otherwise turn every download
   * partial, with every resume failing the same files again.
   */
  const unavailable = [];
  let cappedAt = null;

  // Read AFTER the pins above: pinning an entry the play cache was holding moves its bytes into the
  // download budget, and starting from a total taken before that would let the cap be overrun by
  // exactly the amount a collection adopted. Those pins are this run's and are not written down
  // yet, so they are handed in rather than left out of the person's own total.
  let usedBytes = await readOwnerUsedBytes(takenThisRun);

  setProgress(collectionId, { completed, total, bytes, state: DOWNLOAD_STATE.DOWNLOADING });

  for (const assetRef of missing) {
    if (signal?.aborted) throw new DOMException('Download cancelled', 'AbortError');
    assertIdentity(epoch);

    const remainingBytes = OFFLINE_MAX_BYTES - usedBytes;
    if (remainingBytes <= 0) {
      cappedAt = usedBytes;
      break;
    }

    try {
      const result = await fetchAndPin(assetRef, {
        signal,
        generation,
        remainingBytes,
        // Whether this collection's pin still EXISTS, which is a question for the ledger and not
        // for the bucket. Bytes can go while the ledger row survives (a play-cache eviction), and
        // then the pin is still ours. What a browser reclaiming the whole origin takes is both —
        // and then `held`, which is recorded in a different database, is the only thing left
        // saying we ever had one. Trusting it alone re-downloaded an evicted collection completely
        // unpinned: it measured zero bytes and the cap was free to evict it again at once, which
        // is exactly what "Download again" exists to undo.
        takePin: !held.has(assetRef.key) || !pinned.has(assetRef.key),
      });
      // Same rule as the pins above: the write took the pin, so it is written down before the
      // identity is re-checked. Recording it afterwards lost it to exactly the throw the check
      // exists for, and left the refcount raised with nothing able to bring it down.
      if (result.outcome === 'stored' && !held.has(assetRef.key)) {
        onPinned(assetRef.key);
        takenThisRun.add(assetRef.key);
      }
      assertIdentity(epoch);

      if (result.outcome === 'refused-cap') {
        cappedAt = usedBytes;
        break;
      }
      if (result.outcome !== 'stored') {
        // The bytes were fetched and then NOT stored — a stale cache generation, or a store that
        // would not open. Counting it as done would report a collection ready that is not, and
        // record a pin on an entry that has none. It is a failure, and it reads as one.
        (assetRef.optional ? unavailable : failed).push(assetRef);
        continue;
      }

      bytes += result.bytes;
      usedBytes += result.bytes;
      completed += 1;
    } catch (error) {
      if (error?.name === 'AbortError' || error?.name === 'StaleIdentityError') throw error;
      // One texture that will not fetch costs that texture. Audio is different — a track with no
      // bytes is not downloaded — and the caller reads `failed` to decide what to say.
      console.warn('[offline] Could not download an asset.', assetRef.kind, assetRef.url, error);
      (assetRef.optional ? unavailable : failed).push(assetRef);
    }
    setProgress(collectionId, { completed, total, bytes });
  }

  return { completed, total, bytes, failed, unavailable, cappedAt };
}

/**
 * What a collection actually occupies, from the ledger.
 *
 * Not the bytes the last run transferred, which is what this used to store. A collection whose
 * every track was already cached transfers nothing and would have reported "0 MB"; a resume that
 * fetched the last file of a large download would have overwritten the whole total with that one
 * file. The question is how much is here, so it is asked of what is here.
 */
async function measureCollectionBytes(manifest) {
  const pinned = await readPinnedCacheEntries();
  let total = 0;
  for (const key of new Set(manifestAssets(manifest).map((item) => item.key))) {
    total += Number(pinned.get(key)) || 0;
  }
  return total;
}

function unresolvedDependencyCount(manifest) {
  let count = manifest?.defaultPairUnresolved ? 1 : 0;
  for (const entry of manifest?.entries || []) {
    count += Array.isArray(entry?.unresolvedDependencies) ? entry.unresolvedDependencies.length : 0;
  }
  return count + (manifest?.failedTrackIds || []).length;
}

/** Release rows are required state, and both settlement and later status must ask identically. */
async function missingStoredReleaseKeys(manifest) {
  const keys = [...new Set((manifest?.releaseKeys || []).filter(Boolean))];
  const rows = await Promise.all(keys.map((key) => readStoredRelease(key).catch(() => null)));
  return keys.filter((_, index) => !rows[index]);
}

/**
 * Settle a run: work out whether it finished, and write the manifest's state, size and pins.
 * Shared by the download and resume paths so the two cannot drift apart about what "ready" means.
 *
 * `ownerKey` is who the run started for, and it travels all the way here because this is the write
 * that records the PINS. Landing it on the arriving person's manifest would hand them refcounts
 * they never raised, and their next removal would release them out from under the person who did.
 */
async function settleRun(collectionId, manifest, outcome, pinnedKeys, ownerKey) {
  // An optional asset that could not be fetched counts towards the total without counting as done —
  // it is accounted for, which is what "complete" asks. See `unavailable` in `realizeAssets`.
  const missingReleases = await missingStoredReleaseKeys(manifest);
  const complete =
    outcome.completed + (outcome.unavailable?.length ?? 0) === outcome.total
    && !outcome.cappedAt
    && !outcome.failed.length
    && unresolvedDependencyCount(manifest) === 0
    && missingReleases.length === 0;

  // What this collection no longer needs.
  //
  // A re-download at a NEWER release is a different set of keys — a new version is a new key by
  // design — and inheriting the old pins without dropping them left every superseded version
  // pinned forever: unreachable, uncounted by `bytes` (which measures only the new manifest), and
  // immune to the play cache's eviction because a pin is exactly what that refuses to evict. A few
  // updates of a large collection would quietly spend the whole 5 GB allowance on releases nobody
  // can reach. Only done on a COMPLETE run: an interrupted one may yet be resumed, and unpinning
  // bytes it is about to want again would make a resume re-fetch what it already had.
  const needed = new Set(manifestAssets(manifest).map((item) => item.key));
  const stale = complete ? [...pinnedKeys].filter((key) => !needed.has(key)) : [];
  const kept = new Set(pinnedKeys);
  for (const key of stale) kept.delete(key);
  const bytes = await measureCollectionBytes(manifest);

  // The manifest is written BEFORE a single pin is released, and nothing destructive happens until
  // it has landed. Releasing first read as an obvious economy and was the same failure this file
  // keeps meeting from the other side: the release is instant, the write is guarded, and an
  // identity that changes across the awaits in between makes the write vanish — leaving A's
  // manifest naming superseded versions whose pins are already gone, with nothing to re-take them.
  // A dropped write throws, so the run abandons itself with every pin exactly as it found them.
  const stored = await patchDownloadedCollection(
    collectionId,
    {
      state: complete ? DOWNLOAD_STATE.READY : DOWNLOAD_STATE.PARTIAL,
      bytes,
      assetCount: outcome.total,
      completedCount: outcome.completed,
      cappedAt: outcome.cappedAt ?? null,
      pinnedKeys: [...kept],
    },
    { expectedOwnerKey: ownerKey },
  );

  // `null` is a manifest that is no longer there — removed while this ran. Its pins went with it,
  // and releasing them a second time would take a shared track from whoever else holds it.
  if (stored && stale.length) {
    await unpinCacheEntries(stale);
    for (const key of stale) pinnedKeys.delete(key);
  }
  return stored;
}


/**
 * Hand back the pins a run took that nothing will ever record.
 *
 * A run abandoned because the identity changed must not write its manifest — those pins and that
 * progress belong to the person who has just left, and writing them into the arriving person's
 * library is the logout the download quietly undoes. But the pins were REAL: `pinCacheEntries`
 * already raised the refcount in the ledger, and a pin no manifest records is one nothing can ever
 * release. It used to be harmless because a change of identity emptied the bucket outright; now
 * that a download outlives its owner's session, an unrecorded pin is bytes that can never be
 * evicted and never be freed.
 *
 * So the run releases exactly what IT took — the keys it pinned, minus the ones the manifest
 * already held before it started, which are recorded and are not this run's to give back.
 */
async function releaseUnrecordedPins(pinnedKeys, held) {
  const taken = [...pinnedKeys].filter((key) => !held.has(key));
  if (!taken.length) return;
  await unpinCacheEntries(taken).catch(() => {});
}

/**
 * The one way out of a run that did not finish: every pin it took is either written down or handed
 * back, and never neither.
 *
 * Which of the two it is used to be decided by the ERROR, and the error cannot decide it.
 * `invalidateOfflineRuns` bumps the epoch AND aborts the controller, so the identity can change
 * while the fetch in flight rejects with a plain `AbortError` — the run then took the ordinary
 * "write down what you pinned and stop" path, that write was silently refused for naming somebody
 * who is no longer here, and every pin the run had raised was left with nothing recording it.
 * Unrecorded pins are unreleasable ones: bytes that can never be evicted and never be freed.
 *
 * So the write is the thing that decides. It is attempted under the owner the run started for, and
 * only a write that actually landed counts as having recorded anything — a refusal, a missing
 * manifest and a failed store all mean the same thing here, and all end with the pins given back.
 */
async function abandonRun(collectionId, pinnedKeys, held, ownerKey) {
  const recorded = await patchDownloadedCollection(
    collectionId,
    { state: DOWNLOAD_STATE.PARTIAL, pinnedKeys: [...pinnedKeys] },
    { expectedOwnerKey: ownerKey },
  ).catch(() => null);
  if (recorded) return;
  await releaseUnrecordedPins(pinnedKeys, held);
}

/**
 * Mirror this person's MIDI mappings for everything in a collection, so an offline boot has them.
 *
 * One slice per orbiter in the roster plus the collection's own — which is also where the
 * collection's MIDI-scope preference is kept, so the preference is warmed by the same pass rather
 * than by a second one that could disagree with it.
 *
 * The MIDI source is reached by a dynamic import, and that is not a style choice. Statically it
 * closes a cycle — the session auth this module's own store sits under imports this file, and the
 * MIDI source reaches back through the learn service to that same auth — and a cycle among modules
 * that all run initialisers at import time resolves to whichever half loaded first, which is a
 * boot-order bug waiting for an unrelated import to reorder it. Here it costs nothing: this is one
 * request on a download that is already making dozens.
 *
 * @param {string} collectionId
 * @param {object} manifest the manifest just built, whose entries name the orbiters
 */
async function warmCollectionConfiguration(collectionId, manifest) {
  const scopeKeys = new Set();
  const collectionKey = makeScopeKey('collection', collectionId);
  if (collectionKey) scopeKeys.add(collectionKey);
  for (const entry of manifest?.entries || []) {
    const key = orbiterScopeKey(entry?.orbiterId);
    if (key) scopeKeys.add(key);
  }
  if (!scopeKeys.size) return;
  await import('../input/midi/midiSliceSource.js')
    .then(({ loadMidiSlices }) => loadMidiSlices([...scopeKeys]))
    .catch((error) => {
      console.warn('[offline] Could not warm this collection\'s MIDI mappings for offline use.', error);
    });
}

/**
 * Store the interface assets, and say so when it could not.
 *
 * Shared by the download and the RESUME path, which matters more than it looks: the visit that
 * installs the worker never has a controller (it is built `clientsClaim: false`), so a person who
 * enabled the feature and downloaded a collection on that same first visit warmed nothing — and
 * nothing ever asked again, because only a download did. A resume is the natural second chance,
 * and by then a controller exists.
 */
async function warmInterfaceAssets(signal) {
  try {
    const ui = await precacheUiAssets({ signal });
    if (ui.skipped === 'no-worker') {
      // Said out loud rather than passed over: it is the difference between "the icons are stored"
      // and "the icons will be blank on the first offline boot", and it is invisible otherwise.
      console.info('[offline] No controlling worker yet, so the interface icons were not stored. They are stored on the next download or resume.');
    } else if (ui.failed) {
      console.warn(`[offline] ${ui.failed} of ${ui.requested} interface assets could not be stored.`);
    }
  } catch (error) {
    if (error?.name === 'AbortError') throw error;
    console.warn('[offline] Could not store the interface assets.', error);
  }
}

/**
 * Download a collection: resolve its manifest, then realize its bytes.
 *
 * @param {{ collectionId: string, name?: string|null, coverImage?: string|null, layout?: object|null, sourceUrl?: string|null, roster: object[] }} collection
 *   `layout` is the saved arrangement, kept so an offline boot is arranged the same way.
 *   `sourceUrl` overrides the address the stored entry URL is rebuilt from — the update path passes
 *   the entry's existing one, because it does not run on a page that has the collection's URL.
 * @returns {Promise<object>} the stored manifest
 */
export async function downloadCollection(collection, { quality = null } = {}) {
  const collectionId = collection?.collectionId;
  if (!collectionId) throw new Error('A collection id is required to download a collection.');

  const inFlight = running.get(collectionId);
  if (inFlight) return inFlight.promise;

  // Registered BEFORE anything is awaited. The control offers Cancel the moment it is asked to
  // download, and a run that only became cancellable after two awaits ignored a cancel pressed in
  // that window — and, for the same reason, let a second call slip past the guard above.
  const controller = new AbortController();
  let settle;
  const promise = new Promise((resolve, reject) => {
    settle = { resolve, reject };
  });
  running.set(collectionId, { controller, promise });
  setProgress(collectionId, {
    state: DOWNLOAD_STATE.DOWNLOADING,
    phase: 'resolving',
    completed: 0,
    total: 0,
    bytes: 0,
  });

  const run = serializeLifecycle(async () => {
    const epoch = identityEpoch;
    if (controller.signal.aborted) throw new DOMException('Download cancelled', 'AbortError');

    // Whose download this is, settled ONCE, before the first request — not re-derived at each
    // write. Re-deriving is what filed one person's download under another: the hydration requests
    // are answered for whoever asked for them, and by the time the manifest and the release rows
    // are written the person here can have changed, at which point each write dutifully looks up
    // the owner row again and stamps somebody else. Every owner-derived write below carries this
    // instead, and the store drops a write that does not match it.
    const ownerKey = await readConfigurationOwnerKey();

    const existing = await readDownloadedCollection(collectionId);
    if (!existing) {
      const downloaded = await listDownloadedCollections();
      if (downloaded.length >= OFFLINE_MAX_COLLECTIONS) {
        throw new OfflineCapError('collection-limit', {
          limit: OFFLINE_MAX_COLLECTIONS,
          current: downloaded.length,
        });
      }
    }

    const used = await readOwnerUsedBytes();
    if (used >= OFFLINE_MAX_BYTES) {
      throw new OfflineCapError('byte-limit', { limit: OFFLINE_MAX_BYTES, used });
    }

    // Asked before the first byte, so the person is answering a question about something they just
    // chose to do rather than a prompt out of nowhere.
    await requestPersistentStorage();

    // A download with no stated quality keeps the one this collection already has. That is what
    // makes an update, a resume and a "download again" carry a lossless collection forward instead
    // of quietly returning it to compressed — the caller that means to CHANGE the quality is the
    // only one that says so.
    const requestedQuality = normalizeOfflineQuality(
      quality ?? existing?.requestedQuality ?? DEFAULT_OFFLINE_QUALITY,
    );

    const manifest = await buildCollectionManifest(collection, {
      signal: controller.signal,
      quality: requestedQuality,
      // The address the download was started at, so the stored entry can be rebuilt from its
      // playable parts. Read HERE, at the moment of downloading, because that is when it is true —
      // by the time a card is tapped the page may be anywhere.
      // The caller's stored address wins over wherever this happens to be running. An update
      // started from the library page has no collection URL in the address bar, so rebuilding from
      // `window.location` there would strip the display choices the original download kept.
      sourceUrl:
        collection?.sourceUrl
        ?? (typeof window !== 'undefined' ? window.location?.href ?? null : null),
      onProgress: (done, total) => setProgress(collectionId, { resolved: done, resolvedTotal: total }),
    });
    assertIdentity(epoch);

    // A collection where nothing turned out to be downloadable — every entry a draft, or a viewer
    // whose payloads carry no compressed variant — used to store an empty manifest, satisfy
    // "everything present" trivially, and sit in the list saying "Available offline · 0 audios"
    // while occupying one of the ten slots. Nothing was stored, so nothing is claimed.
    if (!manifest.entries.length) {
      throw new OfflineCapError('nothing-downloadable', {
        skippedTrackIds: manifest.skippedTrackIds,
      });
    }

    // ── The refusal, before a byte moves ─────────────────────────────────────────────────────────
    //
    // The runtime cap below stops a download that has already spent the transfer to reach the
    // ceiling. Lossless makes that the normal case rather than the edge one — five gigabytes is
    // about eight hours of it — so the question is asked here instead, where the answer costs one
    // byte per object and refusing is free.
    //
    // Only keys this person is not already paying for are counted: bytes another of their own
    // collections pins are theirs already, and charging for them twice would refuse a download that
    // fits. A replacement is charged its PEAK, because the old copy is released only after the new
    // one is stored, so the largest track is briefly held twice.
    //
    // An object that will not state its length leaves the total incomplete, and an incomplete total
    // is not grounds for a refusal — the run proceeds and meets the runtime cap if it must. Refusing
    // on a number we know to be short is the arithmetic guess this replaced.
    setProgress(collectionId, { phase: 'measuring' });
    const plannedAssets = manifestAssets(manifest);
    const ownerPinned = await readOwnerPinnedKeys();
    // AUDIO only, for two reasons. It is what the cap is about — a lossless collection is a gigabyte
    // of audio and a few megabytes of everything else — and it is the only kind served by our own
    // proxy, which takes the credentials and the `Range` header this probe needs. Textures and
    // covers are cross-origin object-store URLs where `Range` is not a safelisted header, so probing
    // them would depend on a CORS preflight the bucket may not allow, and a single refusal would
    // turn the whole check off.
    const chargeable = plannedAssets.filter(
      (item) => item.kind === 'audio' && !ownerPinned.has(item.key),
    );
    const measurement = await measureAssets(chargeable, { signal: controller.signal });
    assertIdentity(epoch);
    if (!measurement.unknown) {
      // No transient headroom is added on top. The old copy's pins are released at the END of the
      // run, so `used` already carries the whole previous collection for the entire transfer —
      // charging for the largest replacement as well would refuse a switch that fits.
      const projected = used + measurement.bytes;
      if (projected > OFFLINE_MAX_BYTES) {
        throw new OfflineCapError('would-not-fit', {
          limit: OFFLINE_MAX_BYTES,
          used,
          required: measurement.bytes,
          shortBy: projected - OFFLINE_MAX_BYTES,
          quality: requestedQuality,
        });
      }
    }

    const { releaseRecords, ...storable } = manifest;
    // Re-downloading a collection that is already partly here inherits its pins rather than taking
    // them again.
    const pinnedKeys = new Set(existing?.pinnedKeys || []);
    await writeDownloadedCollection(
      {
        ...storable,
        state: DOWNLOAD_STATE.DOWNLOADING,
        downloadedAt: existing?.downloadedAt ?? Date.now(),
        pinnedKeys: [...pinnedKeys],
      },
      { expectedOwnerKey: ownerKey },
    );
    // The release rows, and — split out of them, under this owner — the personal sync layer each
    // one was resolved with. See `offlineLibraryStore`'s note on the personal layer.
    await writeStoredReleases(releaseRecords, { expectedOwnerKey: ownerKey });
    // Both of those refuse to commit for anybody but `ownerKey`, so nothing has been misfiled — but
    // if the person here changed, nothing has been WRITTEN either, and carrying on would pin bytes
    // no manifest is left to record. Stop instead, and hand the pins back on the way out.
    await assertOwner(ownerKey);
    assertIdentity(epoch);

    // The configuration a download needs that no release carries: this person's MIDI mappings for
    // the collection and for every orbiter in it.
    //
    // Warmed HERE rather than left to the mirror-on-read, because a mirror built only from what
    // somebody happened to open is incomplete by construction: download a collection, never open
    // track fourteen, go offline, and track fourteen's orbiter has no mappings. The whole roster is
    // asked for once, while there is still a network to ask.
    //
    // Never fatal. A controller mapping that could not be warmed is a mapping that falls back to
    // the server next time there is one — not a collection that failed to download.
    setProgress(collectionId, { phase: 'configuration' });
    await warmCollectionConfiguration(collectionId, manifest);
    assertIdentity(epoch);

    // The design library's icons, before the bytes. They are shared by every download and stored
    // once, so this is a cost the FIRST download pays; doing it up front means a download
    // interrupted halfway still leaves an app whose buttons have faces on the next offline boot.
    // Never fatal — an icon that will not fetch is a blank glyph, not an undownloaded collection.
    setProgress(collectionId, { phase: 'interface' });
    await warmInterfaceAssets(controller.signal);
    assertIdentity(epoch);

    setProgress(collectionId, { phase: 'downloading' });
    const held = new Set(pinnedKeys);
    try {
      const outcome = await realizeAssets(collectionId, manifestAssets(manifest), {
        signal: controller.signal,
        epoch,
        held,
        onPinned: (key) => pinnedKeys.add(key),
      });
      // Asked again before the run's last write, for the same reason it was asked before its first.
      await assertOwner(ownerKey);
      const stored = await settleRun(collectionId, manifest, outcome, pinnedKeys, ownerKey);
      setProgress(collectionId, {
        state: stored?.state ?? DOWNLOAD_STATE.PARTIAL,
        phase: 'done',
        ...outcome,
      });
      if (outcome.cappedAt) {
        throw new OfflineCapError('byte-limit', {
          limit: OFFLINE_MAX_BYTES,
          used: outcome.cappedAt,
          manifest: stored,
        });
      }
      return stored;
    } catch (error) {
      // Whatever pins were taken are recorded even on the way out — an abort that dropped them left
      // the collection holding refcounts nothing knew about, which the next attempt would double.
      await abandonRun(collectionId, pinnedKeys, held, ownerKey);
      throw error;
    } finally {
      await refreshOfflineSnapshot();
    }
  });

  run.then(settle.resolve, settle.reject);
  try {
    return await promise;
  } finally {
    if (running.get(collectionId)?.controller === controller) running.delete(collectionId);
    notify();
  }
}

/** Stop a running download. What it already stored stays — the collection becomes `partial`. */
export async function cancelCollectionDownload(collectionId) {
  const inFlight = running.get(collectionId);
  if (!inFlight) return false;
  inFlight.controller.abort();
  try {
    await inFlight.promise;
  } catch {
    // The abort is the expected outcome here.
  }
  await patchDownloadedCollection(collectionId, { state: DOWNLOAD_STATE.PARTIAL });
  await refreshOfflineSnapshot();
  setProgress(collectionId, { state: DOWNLOAD_STATE.PARTIAL, phase: 'cancelled' });
  return true;
}

function entryStoredReleaseKey(entry) {
  if (entry?.entityType === 'orbiter') {
    return releaseRecordKey({ kind: RELEASE_KIND.ORBITER, id: entry.orbiterId, version: entry.orbiterVersion });
  }
  if (entry?.entityType === 'world') {
    return releaseRecordKey({
      kind: RELEASE_KIND.WORLD,
      id: entry.entangledWorldId,
      version: entry.entangledWorldVersion,
    });
  }
  return releaseRecordKey({ kind: RELEASE_KIND.TRACK, id: entry?.trackId, version: entry?.trackVersion });
}

function mergeAssets(...groups) {
  const byKey = new Map();
  for (const item of groups.flat()) {
    if (item?.key) byKey.set(item.key, item);
  }
  return [...byKey.values()];
}

/** Repair metadata dependencies before Resume repairs bytes, while retaining every existing pin. */
async function repairManifestDependencies(manifest, { signal, ownerKey }) {
  const missingReleaseKeys = new Set(await missingStoredReleaseKeys(manifest));
  const releaseRecords = [];
  const entries = [];

  for (const entry of manifest.entries || []) {
    const releaseKey = entryStoredReleaseKey(entry);
    const needsDependencyRetry = (entry.unresolvedDependencies || []).length > 0;
    if (entry.entityType === 'track' || (!entry.entityType && entry.trackId)) {
      if (needsDependencyRetry || missingReleaseKeys.has(releaseKey)) {
        try {
          const payload = await fetchTrackRelease(entry.trackId, {
            version: entry.trackVersion,
            hydrate: 2,
            force: true,
            signal,
          });
          const repaired = payload
            ? await repairTrackManifestEntry(entry, payload, { signal })
            : null;
          if (repaired) {
            entries.push(repaired.entry);
            releaseRecords.push({
              key: entryStoredReleaseKey(repaired.entry),
              payload: repaired.payload,
            });
            continue;
          }
        } catch (error) {
          if (error?.name === 'AbortError') throw error;
        }
      }
      entries.push(entry);
      continue;
    }

    if (missingReleaseKeys.has(releaseKey)) {
      try {
        const payload = entry.entityType === 'orbiter'
          ? await fetchOrbiterRelease(entry.orbiterId, {
            version: entry.orbiterVersion,
            useFallback: false,
            signal,
          })
          : await fetchEntangledWorldRelease(entry.entangledWorldId, {
            version: entry.entangledWorldVersion,
            signal,
          });
        if (payload?.release) releaseRecords.push({ key: releaseKey, payload });
      } catch (error) {
        if (error?.name === 'AbortError') throw error;
      }
    }
    entries.push(entry);
  }

  const stillFailed = [];
  const retryableIds = new Set((manifest.failedEntries || []).map(
    (entry) => entry?.trackId || entry?.orbiterId || entry?.entangledWorldId,
  ).filter(Boolean));
  // Older manifests retained only the id. There is not enough roster identity to rebuild those
  // entries safely, so keep them unresolved instead of accidentally clearing the READY blocker.
  const stillFailedIds = (manifest.failedTrackIds || []).filter((id) => !retryableIds.has(id));
  for (const rosterEntry of manifest.failedEntries || []) {
    if (!rosterEntry?.trackId) {
      stillFailed.push(rosterEntry);
      stillFailedIds.push(rosterEntry?.orbiterId || rosterEntry?.entangledWorldId);
      continue;
    }
    try {
      const payload = await fetchTrackRelease(rosterEntry.trackId, {
        hydrate: 2,
        force: true,
        signal,
      });
      const repaired = payload
        ? await repairTrackManifestEntry(rosterEntry, payload, { signal })
        : null;
      if (!repaired) throw new Error('The track release lookup did not answer.');
      entries.push(repaired.entry);
      releaseRecords.push({
        key: entryStoredReleaseKey(repaired.entry),
        payload: repaired.payload,
      });
    } catch (error) {
      if (error?.name === 'AbortError') throw error;
      stillFailed.push(rosterEntry);
      stillFailedIds.push(rosterEntry.trackId);
    }
  }

  let next = {
    ...manifest,
    entries,
    failedEntries: stillFailed,
    failedTrackIds: stillFailedIds,
  };

  const defaultReleaseKeys = [
    releaseRecordKey({
      kind: RELEASE_KIND.ORBITER,
      id: manifest.defaultPair?.orbiterId,
      version: manifest.defaultPair?.orbiterVersion,
    }),
    releaseRecordKey({
      kind: RELEASE_KIND.WORLD,
      id: manifest.defaultPair?.worldId,
      version: manifest.defaultPair?.worldVersion,
    }),
  ].filter(Boolean);
  if (manifest.defaultPairUnresolved || defaultReleaseKeys.some((key) => missingReleaseKeys.has(key))) {
    const defaults = await resolveDefaultPairManifestDependency({ signal });
    if (!defaults.unresolved) {
      releaseRecords.push(...defaults.releaseRecords);
      next = {
        ...next,
        defaultPair: defaults.pair,
        defaultPairUnresolved: false,
        assets: mergeAssets(next.assets || [], defaults.assets),
      };
    }
  }

  const releaseKeys = [...new Set([
    ...(next.releaseKeys || []),
    ...releaseRecords.map((record) => record.key),
  ].filter(Boolean))];
  next = { ...next, releaseKeys };

  if (releaseRecords.length) await writeStoredReleases(releaseRecords, { expectedOwnerKey: ownerKey });
  await patchDownloadedCollection(manifest.collectionId, {
    entries: next.entries,
    assets: next.assets,
    releaseKeys: next.releaseKeys,
    failedEntries: next.failedEntries,
    failedTrackIds: next.failedTrackIds,
    defaultPair: next.defaultPair,
    defaultPairUnresolved: next.defaultPairUnresolved,
  }, { expectedOwnerKey: ownerKey });
  return next;
}

/**
 * Finish a download that stopped — by a closed tab, a cancel, or the cap.
 *
 * Resume is re-checking presence, not replaying a saved position: what is present is what got done,
 * and nothing else is authoritative about it. The manifest is NOT rebuilt, so a resumed download
 * finishes the versions it started on rather than silently moving to a release published in
 * between.
 */
export async function resumeCollectionDownload(collectionId) {
  if (!collectionId) return null;
  const inFlight = running.get(collectionId);
  if (inFlight) return inFlight.promise;

  const controller = new AbortController();
  let settle;
  const promise = new Promise((resolve, reject) => {
    settle = { resolve, reject };
  });
  running.set(collectionId, { controller, promise });

  const run = serializeLifecycle(async () => {
    const epoch = identityEpoch;
    if (controller.signal.aborted) throw new DOMException('Download cancelled', 'AbortError');

    // Whose resume this is, settled before the first byte — see the note on the download path.
    const ownerKey = await readConfigurationOwnerKey();
    let manifest = await readDownloadedCollection(collectionId);
    if (!manifest) return null;

    manifest = await repairManifestDependencies(manifest, {
      signal: controller.signal,
      ownerKey,
    });
    assertIdentity(epoch);

    // The second chance the first-ever download never had — see `warmInterfaceAssets`.
    setProgress(collectionId, { state: DOWNLOAD_STATE.DOWNLOADING, phase: 'interface' });
    await warmInterfaceAssets(controller.signal);

    setProgress(collectionId, { state: DOWNLOAD_STATE.DOWNLOADING, phase: 'downloading' });
    const pinnedKeys = new Set(manifest.pinnedKeys || []);
    const held = new Set(pinnedKeys);
    try {
      const outcome = await realizeAssets(collectionId, manifestAssets(manifest), {
        signal: controller.signal,
        epoch,
        held,
        onPinned: (key) => pinnedKeys.add(key),
      });
      await assertOwner(ownerKey);
      const stored = await settleRun(collectionId, manifest, outcome, pinnedKeys, ownerKey);
      setProgress(collectionId, {
        state: stored?.state ?? DOWNLOAD_STATE.PARTIAL,
        phase: 'done',
        ...outcome,
      });
      return stored;
    } catch (error) {
      // Same rule as the download path — see `abandonRun`.
      await abandonRun(collectionId, pinnedKeys, held, ownerKey);
      throw error;
    } finally {
      await refreshOfflineSnapshot();
    }
  });

  run.then(settle.resolve, settle.reject);
  try {
    return await promise;
  } finally {
    if (running.get(collectionId)?.controller === controller) running.delete(collectionId);
    notify();
  }
}

/**
 * Remove a downloaded collection.
 *
 * Releases exactly the pins it took — one each — and forgets the manifest. A key another collection
 * still holds keeps a pin and keeps its bytes; a key nobody holds is back under the play cap, which
 * evicts it in its own time. Nothing is deleted here, and that is the point: deletion by collection
 * is how a shared track goes missing from the collection that still wants it.
 *
 * The manifest is read and DELETED before a single pin is released, inside the one lifecycle queue.
 * Two removals that both read before either deleted would each release a pin for the one holder
 * between them, taking a shared track from the collection still holding it.
 */
export async function removeDownloadedCollection(collectionId) {
  if (!collectionId) return false;
  if (running.has(collectionId)) await cancelCollectionDownload(collectionId);

  return serializeLifecycle(async () => {
    const manifest = await readDownloadedCollection(collectionId);
    if (!manifest) return false;

    // Claim it first: from here on no other run can read this manifest and release its pins again.
    await deleteDownloadedCollection(collectionId);

    // Fall back to the keys it would have taken for a manifest with no pin record — minus anything
    // a manifest that is STILL here records a pin on. The subtraction is what makes the fallback
    // safe now that a browser holds more than one person's library: a pin belongs to whoever
    // raised it, and releasing one this collection never took would drop a track out of somebody
    // else's download. What no remaining manifest claims is this one's to release.
    let keys = manifest.pinnedKeys || [];
    if (!keys.length) {
      const held = await readHeldPinnedKeys();
      keys = manifestAssets(manifest)
        .map((item) => item.key)
        .filter((key) => !held.has(key));
    }
    await unpinCacheEntries(keys);
    await pruneStoredReleases();
    await refreshOfflineSnapshot();
    progress.delete(collectionId);
    notify();
    return true;
  });
}


// ── Reading what is here ─────────────────────────────────────────────────────────────────────────

/**
 * One collection's real state: its manifest, checked against the bucket.
 *
 * `ready` is computed, never read from the stored state — a manifest that says ready and a bucket
 * that has lost half its entries is exactly the situation this check exists for, and the honest
 * answer is "partial, resume it".
 */
/**
 * Give a download made before covers were kept its picture.
 *
 * One fetch of the collection for its cover URL, one asset stored and pinned, one manifest patch.
 * Never a re-download and never a version change — a card without a picture is not a reason to
 * take a newer release, which stays the person's decision (`offlineUpdates.js`). A no-op for a
 * download that already has a cover, or a collection that has none to give.
 *
 * @param {string} collectionId
 * @param {{ fetchCollection: (id: string) => Promise<any> }} deps
 */
export async function refreshCollectionCover(collectionId, { fetchCollection }) {
  const existing = await readDownloadedCollection(collectionId).catch(() => null);
  if (!existing || existing.coverKey) return existing;
  const live = await fetchCollection(collectionId);
  const cover = collectionCoverAsset(live?.coverImage ?? null);
  if (!cover) return existing;

  const epoch = identityEpoch;
  const pinnedKeys = new Set(existing.pinnedKeys || []);
  const outcome = await realizeAssets(collectionId, [cover], {
    signal: new AbortController().signal,
    epoch,
    held: new Set(pinnedKeys),
    onPinned: (key) => pinnedKeys.add(key),
  });
  assertIdentity(epoch);

  // `realizeAssets` reports a refused or failed asset, it does not throw. Recording `coverKey` for
  // bytes that never landed would put this collection into `partial` for good: the status check
  // counts the cover among the manifest's assets and finds it missing, and the page's retry guard
  // is `coverKey` itself, so nothing would ever look again. Leave the manifest untouched instead —
  // the picture is still missing, which is exactly what the next visit should find.
  if (outcome.failed.length > 0 || outcome.unavailable.length > 0 || outcome.cappedAt) {
    progress.delete(collectionId);
    return existing;
  }
  const assets = [...(existing.assets || []).filter((item) => item?.key !== cover.key), cover];
  const next = { ...existing, coverImage: cover.url, coverKey: cover.identity, assets };
  const stored = await patchDownloadedCollection(collectionId, {
    coverImage: cover.url,
    coverKey: cover.identity,
    assets,
    pinnedKeys: [...pinnedKeys],
    bytes: await measureCollectionBytes(next),
  });
  // `realizeAssets` opened a 0/1 progress entry to report itself. Only `removeDownloadedCollection`
  // ever clears that map, so leaving it here would show a download stuck at 0/1 on this card for
  // the rest of the document's life.
  progress.delete(collectionId);
  notify(OFFLINE_CHANGE.LIBRARY);
  return stored;
}

export async function readCollectionOfflineStatus(collectionId, context = null) {
  const manifest = await readDownloadedCollection(collectionId);
  if (!manifest) return null;

  const assets = manifestAssets(manifest);
  const present = await readPresentCacheKeys(assets.map((item) => item.key));
  const missing = assets.filter((item) => !present.has(item.key));
  const missingRequired = missing.filter((item) => item.optional !== true);
  const missingReleaseCount = (await missingStoredReleaseKeys(manifest)).length;
  const unresolvedEntryCount = unresolvedDependencyCount(manifest);

  // AUDIO is the difference between "some of this is missing" and "this will not play". A lost
  // cover is a card without a picture; a lost track is a stage that plays silence, and a person
  // told "available offline" about a collection in that state has been told something false.
  // Browsers reclaim script-writable storage under disk pressure and `persist()` is a request, not
  // a promise, so this is a state the feature has to be able to say out loud.
  const audioKeys = new Set();
  for (const entry of manifest.entries || []) {
    for (const identity of entry.audio || []) {
      const key = toCacheRequestUrl(identity);
      if (key) audioKeys.add(key);
    }
  }
  const missingAudioCount = [...audioKeys].filter((key) => !present.has(key)).length;
  const hasAudioFreeEntry = (manifest.entries || []).some(
    (entry) => entry?.entityType === 'orbiter' || entry?.entityType === 'world',
  );

  return {
    collectionId,
    kind: manifest.kind ?? 'collection',
    name: manifest.name ?? null,
    coverImage: manifest.coverImage ?? null,
    // The stored cover's logical key. Null for a download made before covers were kept — the page
    // falls back to the remote URL there, which is the best that can be done for those.
    coverKey: manifest.coverKey ?? null,
    // Where a card opens. Rebuilt for a manifest stored before this field existed, so an older
    // download opens at the right place instead of at a card that does nothing.
    entryUrl: manifest.entryUrl ?? buildOfflineEntryUrl(collectionId),
    entryCount: manifest.entries?.length ?? 0,
    // What this was downloaded at: 'compressed' or 'lossless'. One answer for the collection — a
    // track without the asked-for variant quietly took the other one, which is not a state worth
    // making anybody read.
    quality: manifestQuality(manifest),
    // What removing this would give back to the allowance — NOT its gross size, which counts bytes
    // other collections are holding too. The number a cap refusal invites someone to act on.
    removalSavings: await readRemovalSavings(collectionId, context),
    // A roster that moved on. Counted rather than merely flagged, because "3 new items" and
    // "newer versions" are different sentences and lead to the same button for different reasons.
    addedTrackCount: manifest.addedTrackCount ?? 0,
    removedTrackCount: manifest.removedTrackCount ?? 0,
    assetCount: assets.length,
    presentCount: present.size,
    missingCount: missing.length,
    missingRequiredCount: missingRequired.length,
    missingReleaseCount,
    missingAudioCount,
    audioCount: audioKeys.size,
    // Nothing left of what was downloaded. Not the same as "partial", and it is the state to say
    // plainly rather than let a person press play and hear nothing.
    //
    // "Never had bytes" is excluded, and that is not a nicety: a download between its manifest
    // write and its first stored file has every audio key missing and looks identical to a
    // reclaimed one. The download control hid that behind its in-memory `running` flag, which is
    // per DOCUMENT — so the library page, which is a separate document (and a framed one), showed a
    // running download as "Storage reclaimed" with an offer to download it again. The stored state
    // is the only signal both documents share.
    evicted:
      audioKeys.size > 0
      && missingAudioCount === audioKeys.size
      && manifest.state !== DOWNLOAD_STATE.DOWNLOADING
      && Number(manifest.completedCount) > 0,
    // Some of it plays. Enough to open, not enough to be called ready.
    playable: hasAudioFreeEntry || (audioKeys.size > 0 && missingAudioCount < audioKeys.size),
    updateAvailable: manifest.updateAvailable === true,
    bytes: Number(manifest.bytes) || 0,
    downloadedAt: manifest.downloadedAt ?? null,
    cappedAt: manifest.cappedAt ?? null,
    skippedTrackIds: manifest.skippedTrackIds ?? [],
    state:
      missingRequired.length === 0 && missingReleaseCount === 0 && unresolvedEntryCount === 0
        ? DOWNLOAD_STATE.READY
        : DOWNLOAD_STATE.PARTIAL,
    running: running.has(collectionId),
    progress: readDownloadProgress(collectionId),
  };
}

/** Every downloaded collection, each checked against the bucket. */
export async function readOfflineLibrary() {
  const manifests = await listDownloadedCollections();
  // Read once for the whole listing. Each row needs both tables to work out what removing it would
  // free, and asking per row made a ten-collection refresh twenty extra full reads.
  const context = { manifests, pinned: await readPinnedCacheEntries() };
  const statuses = [];
  for (const manifest of manifests) {
    const status = await readCollectionOfflineStatus(manifest.collectionId, context);
    if (status) statuses.push(status);
  }
  return {
    collections: statuses,
    maxCollections: OFFLINE_MAX_COLLECTIONS,
    storage: await readStorageStatus(),
  };
}
